import React, { useState, useEffect } from 'react';
import Header from './Header.jsx';
import { motion, AnimatePresence } from 'framer-motion';
import CategoryComponent from './CategoryComponent.jsx';
import CurrencyComponent from './CurrencyComponent.jsx';
import TicketTypeComponent from './TicketTypeComponent.jsx';
import TicketCategoryComponent from './TicketCategoryComponent.jsx';
import ContactComponent from './ContactComponent.jsx';
import FeedbackComponent from './FeedbackComponent.jsx';
import HowItWorksComponent from './HowItWorksComponent.jsx';
import AnalyticsComponent from './AnalyticsComponent.jsx';
import UserManagementComponent from './UserManagementComponent.jsx';
import Footer from './Footer.jsx';
import { 
    FolderOpen, DollarSign, Ticket, Tag, Info, MessageSquare, 
    Phone, Settings, Shield, Users, BarChart3, Activity,
    ChevronRight, Home, LogOut, Menu, X, Plus
} from 'lucide-react';
import { domain } from './Domain.jsx';
import useFetch from './useFetch.jsx';

const Admin = () => {
  const [activeView, setActiveView] = useState("Event Categories");
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  // Fetch statistics from backend
  const { data: stats, loading: statsLoading, error: statsError } = useFetch(`${domain}/statistics`);
  
  // Fetch module-specific data
  const { data: eventCategoriesData, loading: categoriesLoading } = useFetch(`${domain}/event-categories`);
  const { data: currenciesData, loading: currenciesLoading } = useFetch(`${domain}/currencies`);
  const { data: ticketTypesData, loading: ticketTypesLoading } = useFetch(`${domain}/ticket-types`);
  const { data: ticketCategoriesData, loading: ticketCategoriesLoading } = useFetch(`${domain}/ticket-categories`);
  const { data: contactsData, loading: contactsLoading } = useFetch(`${domain}/contact`);
  const { data: feedbackData, loading: feedbackLoading } = useFetch(`${domain}/feedback`);

  // Extract the actual data arrays from the responses
  const eventCategories = Array.isArray(eventCategoriesData) ? eventCategoriesData : [];
  const currencies = Array.isArray(currenciesData) ? currenciesData : [];
  const ticketTypes = Array.isArray(ticketTypesData) ? ticketTypesData : [];
  const ticketCategories = Array.isArray(ticketCategoriesData) ? ticketCategoriesData : [];
  const contacts = Array.isArray(contactsData) ? contactsData : [];

  // Handle potential error responses
  const handleApiData = (data, defaultValue = []) => {
    if (!data) return defaultValue;
    if (Array.isArray(data)) return data;
    if (data.error) {
      console.error('API Error:', data.error);
      return defaultValue;
    }
    if (data.data && Array.isArray(data.data)) return data.data;
    return defaultValue;
  };

  // Process data with error handling
  const processedEventCategories = handleApiData(eventCategoriesData);
  const processedCurrencies = handleApiData(currenciesData);
  const processedTicketTypes = handleApiData(ticketTypesData);
  const processedTicketCategories = handleApiData(ticketCategoriesData);
  const processedContacts = handleApiData(contactsData);
  const processedFeedback = handleApiData(feedbackData);

  // Debug logging to see what data is being returned
  useEffect(() => {
    console.log('=== EVENT CATEGORIES DEBUG ===');
    console.log('Raw Event Categories Data:', eventCategoriesData);
    console.log('Processed Event Categories:', processedEventCategories);
    console.log('Categories Loading:', categoriesLoading);
    console.log('Categories Length:', processedEventCategories?.length);
    
    // Check if there's an error
    if (eventCategoriesData && typeof eventCategoriesData === 'object' && 'error' in eventCategoriesData) {
      console.error('API Error:', eventCategoriesData.error);
    }
    
    // Log the first few categories if they exist
    if (processedEventCategories && processedEventCategories.length > 0) {
      console.log('First 3 categories:', processedEventCategories.slice(0, 3));
    }
  }, [eventCategoriesData, processedEventCategories, categoriesLoading]);

  const adminModules = [
    { 
      name: "Event Categories", 
      icon: <FolderOpen className="w-5 h-5" />, 
      description: "Manage event categories and classifications",
      color: "from-orange-400 to-orange-500",
      bgColor: "bg-orange-50",
      iconColor: "text-orange-600"
    },
    { 
      name: "Currencies", 
      icon: <DollarSign className="w-5 h-5" />, 
      description: "Supported currencies and exchange rates",
      color: "from-teal-400 to-teal-500",
      bgColor: "bg-teal-50",
      iconColor: "text-teal-600"
    },
    { 
      name: "Ticket Type", 
      icon: <Ticket className="w-5 h-5" />, 
      description: "Define different types of tickets available",
      color: "from-orange-400 to-orange-500",
      bgColor: "bg-orange-50",
      iconColor: "text-orange-600"
    },
    { 
      name: "Ticket Category", 
      icon: <Tag className="w-5 h-5" />, 
      description: "Organize tickets into categories and tiers",
      color: "from-teal-400 to-teal-500",
      bgColor: "bg-teal-50",
      iconColor: "text-teal-600"
    },
    { 
      name: "How It Works", 
      icon: <Info className="w-5 h-5" />, 
      description: "Manage help content and user guides",
      color: "from-orange-400 to-orange-500",
      bgColor: "bg-orange-50",
      iconColor: "text-orange-600"
    },
    { 
      name: "Feedback", 
      icon: <MessageSquare className="w-5 h-5" />, 
      description: "Review and respond to user feedback",
      color: "from-teal-400 to-teal-500",
      bgColor: "bg-teal-50",
      iconColor: "text-teal-600"
    },
    { 
      name: "Contact", 
      icon: <Phone className="w-5 h-5" />, 
      description: "Manage contact information and support",
      color: "from-orange-400 to-orange-500",
      bgColor: "bg-orange-50",
      iconColor: "text-orange-600"
    },
    { 
      name: "Analytics", 
      icon: <BarChart3 className="w-5 h-5" />, 
      description: "View detailed reports and analytics",
      color: "from-green-400 to-green-500",
      bgColor: "bg-green-50",
      iconColor: "text-green-600"
    },
    { 
      name: "User Management", 
      icon: <Users className="w-5 h-5" />, 
      description: "Manage user accounts and permissions",
      color: "from-purple-400 to-purple-500",
      bgColor: "bg-purple-50",
      iconColor: "text-purple-600"
    }
  ];

  // Check if user is admin (you can add your admin check logic here)
  const [isAdmin, setIsAdmin] = useState(true);

  // Admin statistics with real data
  const adminStats = {
    totalEvents: stats?.total_events || 0,
    totalUsers: stats?.total_users || 0,
    activeUsers: stats?.active_users || 0,
    totalCategories: eventCategories?.length || 0,
    totalFeedback: stats?.total_feedback || 0,
    totalTicketsSold: stats?.total_tickets_sold || 0,
    averageRating: stats?.average_rating || 0,
    totalTutorials: stats?.total_tutorials || 0,
    totalTutorialViews: stats?.total_tutorial_views || 0
  };

  useEffect(() => {
    const checkScreenSize = () => {
      setIsMobile(window.innerWidth < 768);
      if (window.innerWidth >= 768) {
        setSidebarOpen(false);
      }
    };

    checkScreenSize();
    window.addEventListener('resize', checkScreenSize);
    return () => window.removeEventListener('resize', checkScreenSize);
  }, []);

  const handleModuleChange = (moduleName) => {
    setActiveView(moduleName);
    if (isMobile) {
      setSidebarOpen(false);
    }
  };

  const renderComponent = () => {
    switch (activeView) {
      case "Event Categories":
        return <CategoryComponent />;
      case "Currencies":
        return <CurrencyComponent />;
      case "Ticket Type":
        return <TicketTypeComponent />;
      case "Ticket Category":
        return <TicketCategoryComponent />;
      case "How It Works":
        return <HowItWorksComponent />;
      case "Feedback":
        return <FeedbackComponent />;
      case "Contact":
        return <ContactComponent />;
      case "Analytics":
        return <AnalyticsComponent />;
      case "User Management":
        return <UserManagementComponent />;
      default:
        return <CategoryComponent />;
    }
  };

  const getActiveModule = () => {
    return adminModules.find(module => module.name === activeView);
  };

  // Get module-specific statistics with real data
  const getModuleStats = () => {
    switch (activeView) {
      case "Event Categories":
        return {
          total: processedEventCategories?.length || 0,
          active: processedEventCategories?.filter(cat => cat.event_count > 0)?.length || 0,
          loading: categoriesLoading
        };
      case "Currencies":
        return {
          total: processedCurrencies?.length || 0,
          active: processedCurrencies?.length || 0, // All currencies are considered active
          loading: currenciesLoading
        };
      case "Ticket Type":
        return {
          total: processedTicketTypes?.length || 0,
          active: processedTicketTypes?.length || 0, // All ticket types are considered active
          loading: ticketTypesLoading
        };
      case "Ticket Category":
        return {
          total: processedTicketCategories?.length || 0,
          active: processedTicketCategories?.length || 0, // All ticket categories are considered active
          loading: ticketCategoriesLoading
        };
      case "How It Works":
        return {
          total: adminStats.totalTutorials,
          active: adminStats.totalTutorialViews,
          loading: statsLoading
        };
      case "Feedback":
        const thisMonthFeedback = processedFeedback?.filter(feedback => {
          const feedbackDate = new Date(feedback.created_at);
          const now = new Date();
          return feedbackDate.getMonth() === now.getMonth() && 
                 feedbackDate.getFullYear() === now.getFullYear();
        }).length || 0;
        return {
          total: processedFeedback?.length || 0,
          active: thisMonthFeedback,
          loading: feedbackLoading
        };
      case "Contact":
        const thisMonthContacts = processedContacts?.filter(contact => {
          const contactDate = new Date(contact.created_at);
          const now = new Date();
          return contactDate.getMonth() === now.getMonth() && 
                 contactDate.getFullYear() === now.getFullYear();
        }).length || 0;
        return {
          total: processedContacts?.length || 0,
          active: thisMonthContacts,
          loading: contactsLoading
        };
      case "Analytics":
        return {
          total: adminStats.totalEvents,
          active: adminStats.totalTicketsSold,
          loading: statsLoading
        };
      case "User Management":
        return {
          total: adminStats.totalUsers,
          active: adminStats.activeUsers,
          loading: statsLoading
        };
      default:
        return {
          total: 0,
          active: 0,
          loading: false
        };
    }
  };

  if (!isAdmin) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Shield className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-gray-800 mb-2">Access Denied</h1>
          <p className="text-gray-600">You don't have permission to access the admin panel.</p>
        </div>
      </div>
    );
  }

  const moduleStats = getModuleStats();

  return (
    <div className="min-h-screen bg-gray-50 overflow-x-hidden">
      <Header />
      
      {/* Mobile Menu Button */}
      <div className="md:hidden fixed top-20 left-4 z-50">
        <button
          onClick={() => setSidebarOpen(!sidebarOpen)}
          className="p-2 bg-white rounded-lg shadow-lg border border-gray-200 hover:bg-gray-50 transition-colors duration-200"
        >
          {sidebarOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>

      {/* Mobile Overlay */}
      <AnimatePresence>
        {sidebarOpen && isMobile && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black bg-opacity-50 z-40 md:hidden"
            onClick={() => setSidebarOpen(false)}
          />
        )}
      </AnimatePresence>

      <div className="flex">
        {/* Sidebar */}
        <motion.div
          initial={isMobile ? { x: -320 } : false}
          animate={isMobile ? { x: sidebarOpen ? 0 : -320 } : false}
          transition={{ type: "spring", damping: 25, stiffness: 200 }}
          className={`fixed md:relative z-50 w-80 h-full bg-gradient-to-b from-white to-gray-50 shadow-2xl border-r border-gray-200 transform transition-transform duration-300 ease-in-out ${
            isMobile ? (sidebarOpen ? 'translate-x-0' : '-translate-x-full') : 'translate-x-0'
          } ${isMobile && !sidebarOpen ? 'hidden' : ''}`}
        >
          <div className="flex flex-col h-full">
            {/* Admin Header */}
            <div className="p-6 border-b border-gray-200 bg-white">
              <div className="flex items-center gap-4 mb-6">
                <div className="w-14 h-14 bg-gradient-to-br from-orange-400 to-orange-500 rounded-2xl flex items-center justify-center shadow-lg">
                  <Settings className="w-7 h-7 !text-white" />
                </div>
                <div>
                  <h1 className="text-lg font-bold text-gray-800 mb-1">Admin Panel</h1>
                  <p className="text-sm text-gray-500">EventPlots Management</p>
                </div>
              </div>
              
              {/* Quick Stats */}
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-gradient-to-br from-orange-50 to-orange-100 rounded-xl p-4 border border-orange-200">
                  <div className="flex items-center gap-2 mb-2">
                    <div className="w-8 h-8 bg-orange-500 rounded-lg flex items-center justify-center">
                      <BarChart3 className="w-4 h-4 !text-white" />
                    </div>
                    <span className="text-xs text-orange-700 font-semibold">Events</span>
                  </div>
                  <p className="text-sm font-bold text-orange-800">
                    {statsLoading ? (
                      <div className="h-6 bg-orange-200 rounded animate-pulse"></div>
                    ) : (
                      adminStats.totalEvents
                    )}
                  </p>
                  <p className="text-xs text-orange-600 mt-1">Total Created</p>
                </div>
                <div className="bg-gradient-to-br from-teal-50 to-teal-100 rounded-xl p-4 border border-teal-200">
                  <div className="flex items-center gap-2 mb-2">
                    <div className="w-8 h-8 bg-teal-500 rounded-lg flex items-center justify-center">
                      <Users className="w-4 h-4 !text-white" />
                    </div>
                    <span className="text-xs text-teal-700 font-semibold">Users</span>
                  </div>
                  <p className="text-sm font-bold text-teal-800">
                    {statsLoading ? (
                      <div className="h-6 bg-teal-200 rounded animate-pulse"></div>
                    ) : (
                      adminStats.totalUsers
                    )}
                  </p>
                  <p className="text-xs text-teal-600 mt-1">Registered</p>
                </div>
              </div>
            </div>

            {/* Navigation Section */}
            <div className="flex-1 p-6 overflow-y-auto">
              {/* Management Modules */}
              <div className="mb-8">
                <div className="flex items-center gap-2 mb-4">
                  <div className="w-1 h-6 bg-gradient-to-b from-orange-500 to-teal-500 rounded-full"></div>
                  <h3 className="text-sm font-bold text-gray-700 uppercase tracking-wider">
                    Management Modules
                  </h3>
                </div>
                <nav className="space-y-3">
                  {adminModules.map((module, index) => (
                    <motion.button
                      key={module.name}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: index * 0.1 }}
                      onClick={() => handleModuleChange(module.name)}
                      className={`w-full group relative overflow-hidden mb-2 ${
                        activeView === module.name
                          ? 'bg-gradient-to-r from-orange-500 to-orange-600 !text-white shadow-lg'
                          : 'hover:bg-gray-50 text-gray-700'
                      } rounded-xl p-4 transition-all duration-300 border ${
                        activeView === module.name 
                          ? 'border-orange-400 shadow-xl' 
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <div className="flex items-start gap-3">
                        <div className={`p-3 rounded-xl flex-shrink-0 ${
                          activeView === module.name 
                            ? 'bg-white/20 backdrop-blur-sm' 
                            : `${module.bgColor} ${module.iconColor}`
                        } transition-all duration-300`}>
                          {module.icon}
                        </div>
                        <div className="flex-1 text-left min-w-0 max-w-none">
                          <p className="font-semibold text-sm mb-1">{module.name}</p>
                          <p className={`text-xs leading-relaxed whitespace-normal break-words hyphens-auto ${
                            activeView === module.name 
                              ? '!text-white/90' 
                              : '!text-gray-500'
                          }`}>
                            {module.description}
                          </p>
                        </div>
                        <div className="flex-shrink-0 ml-2">
                          <ChevronRight className={`w-5 h-5 transition-all duration-300 ${
                            activeView === module.name 
                              ? 'rotate-90 !text-white' 
                              : 'text-gray-400 group-hover:text-gray-600 group-hover:translate-x-1'
                          }`} />
                        </div>
                      </div>
                      {activeView === module.name && (
                        <motion.div
                          layoutId="activeTab"
                          className="absolute inset-0 bg-gradient-to-r from-orange-500/10 to-orange-600/10 rounded-xl"
                          initial={false}
                          transition={{ type: "spring", stiffness: 500, damping: 30 }}
                        />
                      )}
                    </motion.button>
                  ))}
                </nav>
              </div>

              {/* Quick Actions */}
              <div className="border-t border-gray-200 pt-6">
                <div className="flex items-center gap-2 mb-4">
                  <div className="w-1 h-6 bg-gradient-to-b from-orange-500 to-teal-500 rounded-full"></div>
                  <h3 className="text-sm font-bold text-gray-700 uppercase tracking-wider">
                    Quick Actions
                  </h3>
                </div>
                <div className="space-y-3">
                  <button className="w-full flex items-center gap-4 p-4 rounded-xl text-left text-gray-700 hover:bg-gray-50 transition-all duration-200 border border-gray-200 hover:border-gray-300 hover:shadow-md">
                    <div className="w-10 h-10 bg-gradient-to-br from-teal-100 to-teal-200 rounded-xl flex items-center justify-center">
                      <Home className="w-5 h-5 text-teal-600" />
                    </div>
                    <div>
                      <span className="font-semibold text-sm">Back to Site</span>
                      <p className="text-xs text-gray-500">Return to main site</p>
                    </div>
                  </button>
                  <button className="w-full flex items-center gap-4 p-4 rounded-xl text-left text-red-600 hover:bg-red-50 transition-all duration-200 border border-red-200 hover:border-red-300 hover:shadow-md">
                    <div className="w-10 h-10 bg-gradient-to-br from-red-100 to-red-200 rounded-xl flex items-center justify-center">
                      <LogOut className="w-5 h-5 text-red-600" />
                    </div>
                    <div>
                      <span className="font-semibold text-sm">Logout</span>
                      <p className="text-xs text-red-500">Sign out of admin</p>
                    </div>
                  </button>
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="p-6 border-t border-gray-200 bg-gray-50">
              <div className="text-center">
                <p className="text-xs text-gray-500 mb-2">EventPlots Admin</p>
                <div className="flex items-center justify-center gap-2">
                  <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                  <span className="text-xs text-green-600 font-medium">System Online</span>
                </div>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Main Content */}
        <div className="flex-1 md:ml-0 bg-gradient-to-br from-gray-50 to-gray-100 min-h-screen max-w-full overflow-x-hidden">
          <div className="p-6 lg:p-8">
            {/* Top Bar */}
            <div className="flex items-center justify-between mb-8">
              <div className="flex items-center gap-4">
                <div className="hidden md:block">
                  <h2 className="text-lg font-bold text-gray-800">Dashboard</h2>
                  <p className="text-sm text-gray-600">Welcome back, Administrator</p>
                </div>
              </div>
              <div className="flex items-center gap-4">
                <div className="hidden md:flex items-center gap-2 bg-white px-4 py-2 rounded-lg shadow-sm border border-gray-200">
                  <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                  <span className="text-sm text-gray-600">Live</span>
                </div>
                <div className="w-10 h-10 bg-white rounded-xl shadow-sm border border-gray-200 flex items-center justify-center">
                  <Users className="w-5 h-5 text-gray-600" />
                </div>
              </div>
            </div>

            {/* Page Header */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="mb-8"
            >
              <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-4 md:p-6 lg:p-8 max-w-full overflow-x-hidden">
                <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 md:gap-6">
                  <div className="flex items-center gap-3 md:gap-6">
                    <div className={`w-12 h-12 md:w-16 md:h-16 rounded-2xl flex items-center justify-center shadow-lg bg-gradient-to-br ${getActiveModule()?.color}`}>
                      {getActiveModule()?.icon}
                    </div>
                    <div>
                      <h1 className="text-lg font-bold text-gray-800 mb-1 md:mb-2">{activeView}</h1>
                      <p className="text-sm text-gray-600">{getActiveModule()?.description}</p>
                    </div>
                  </div>
                  
                  {/* Quick Stats for Current Module */}
                  <div className="flex gap-2 md:gap-4">
                    <div className="bg-gradient-to-br from-orange-50 to-orange-100 rounded-xl p-3 md:p-4 border border-orange-200">
                      <div className="flex items-center gap-1 md:gap-2 mb-1">
                        <BarChart3 className="w-3 h-3 md:w-4 md:h-4 text-orange-600" />
                        <span className="text-xs text-orange-700 font-semibold">
                          {activeView === "How It Works" ? "Tutorials" : 
                           activeView === "Contact" ? "Messages" :
                           activeView === "Feedback" ? "Reviews" : "Total"}
                        </span>
                      </div>
                      <p className="text-sm font-bold text-orange-800">
                        {moduleStats.loading ? (
                          <div className="h-4 md:h-6 bg-orange-200 rounded animate-pulse"></div>
                        ) : (
                          moduleStats.total
                        )}
                      </p>
                    </div>
                    <div className="bg-gradient-to-br from-teal-50 to-teal-100 rounded-xl p-3 md:p-4 border border-teal-200">
                      <div className="flex items-center gap-1 md:gap-2 mb-1">
                        <Activity className="w-3 h-3 md:w-4 md:h-4 text-teal-600" />
                        <span className="text-xs text-teal-700 font-semibold">
                          {activeView === "How It Works" ? "Views" : 
                           activeView === "Contact" ? "This Month" :
                           activeView === "Feedback" ? "This Month" : "Active"}
                        </span>
                      </div>
                      <p className="text-sm font-bold text-teal-800">
                        {moduleStats.loading ? (
                          <div className="h-4 md:h-6 bg-teal-200 rounded animate-pulse"></div>
                        ) : (
                          moduleStats.active
                        )}
                      </p>
                    </div>
                  </div>
                </div>
                
                {/* Enhanced Breadcrumb */}
                <nav className="flex items-center gap-1 md:gap-2 mt-4 md:mt-6 pt-4 md:pt-6 border-t border-gray-100">
                  <div className="flex items-center gap-1 md:gap-2 text-xs md:text-sm text-gray-500">
                    <div className="w-6 h-6 md:w-8 md:h-8 bg-gray-100 rounded-lg flex items-center justify-center">
                      <Settings className="w-3 h-3 md:w-4 md:h-4 text-gray-600" />
                    </div>
                    <span className="font-medium">Admin Panel</span>
                  </div>
                  <ChevronRight className="w-3 h-3 md:w-4 md:h-4 text-gray-400" />
                  <div className="flex items-center gap-1 md:gap-2 text-xs md:text-sm">
                    <div className={`w-6 h-6 md:w-8 md:h-8 rounded-lg flex items-center justify-center bg-gradient-to-br ${getActiveModule()?.color}`}>
                      {getActiveModule()?.icon}
                    </div>
                    <span className="text-gray-800 font-semibold">{activeView}</span>
                  </div>
                </nav>
              </div>
            </motion.div>

            {/* Content Area */}
            <motion.div
              key={activeView}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3 }}
              className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden"
            >
              {/* Content Header */}
              <div className="bg-gradient-to-r from-gray-50 to-gray-100 px-4 md:px-6 lg:px-8 py-3 md:py-4 border-b border-gray-200">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-800">{activeView} Management</h3>
                    <p className="text-sm text-gray-600">Manage and configure {activeView.toLowerCase()}</p>
                  </div>
                  <div className="flex items-center gap-2 md:gap-3">
                    <button className="px-3 py-2 md:px-4 md:py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg text-sm font-medium transition-colors duration-200 flex items-center gap-1 md:gap-2">
                      <Activity className="w-4 h-4" />
                      <span className="hidden sm:inline">Refresh</span>
                    </button>
                    <button className="px-3 py-2 md:px-4 md:py-2 bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 !text-white rounded-lg text-sm font-medium transition-all duration-200 flex items-center gap-1 md:gap-2 shadow-md hover:shadow-lg">
                      <Plus className="w-4 h-4" />
                      <span className="hidden sm:inline">Add New</span>
                    </button>
                  </div>
                </div>
              </div>

              {/* Main Content */}
              <div className="p-4 md:p-6 lg:p-8">
                <div className="bg-gray-50 rounded-xl p-4 md:p-6 border border-gray-200">
                  {renderComponent()}
                </div>
              </div>

              {/* Content Footer */}
              <div className="bg-gray-50 px-6 lg:px-8 py-4 border-t border-gray-200">
                <div className="flex items-center justify-between text-sm text-gray-600">
                  <div className="flex items-center gap-4">
                    <span>Last updated: {new Date().toLocaleString()}</span>
                    <span>•</span>
                    <span>Auto-save enabled</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                    <span className="text-green-600 font-medium">All systems operational</span>
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Quick Actions Panel */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-6"
            >
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center">
                    <BarChart3 className="w-5 h-5 text-orange-600" />
                  </div>
                  <div>
                    <h4 className="text-lg font-semibold text-gray-800">Analytics</h4>
                    <p className="text-sm text-gray-600">View detailed reports</p>
                  </div>
                </div>
                <button 
                  onClick={() => handleModuleChange("Analytics")}
                  className="w-full bg-orange-50 hover:bg-orange-100 text-orange-700 px-4 py-2 rounded-lg text-sm font-medium transition-colors duration-200"
                >
                  View Reports
                </button>
              </div>

              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 bg-teal-100 rounded-lg flex items-center justify-center">
                    <Users className="w-5 h-5 text-teal-600" />
                  </div>
                  <div>
                    <h4 className="text-lg font-semibold text-gray-800">Users</h4>
                    <p className="text-sm text-gray-600">Manage user accounts</p>
                  </div>
                </div>
                <button 
                  onClick={() => handleModuleChange("User Management")}
                  className="w-full bg-teal-50 hover:bg-teal-100 text-teal-700 px-4 py-2 rounded-lg text-sm font-medium transition-colors duration-200"
                >
                  Manage Users
                </button>
              </div>

              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center">
                    <Settings className="w-5 h-5 text-orange-600" />
          </div>
          <div>
                    <h4 className="text-lg font-semibold text-gray-800">Settings</h4>
                    <p className="text-sm text-gray-600">System configuration</p>
                  </div>
                </div>
                <button className="w-full bg-orange-50 hover:bg-orange-100 text-orange-700 px-4 py-2 rounded-lg text-sm font-medium transition-colors duration-200">
                  Configure
                </button>
              </div>
            </motion.div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default Admin;